import React, { useEffect, useRef, useState } from "react";

import useDebounce from "hooks/useDebounce";
import { Button } from "primereact/button";
import { Calendar, CalendarProps } from "primereact/calendar";
import { T } from "services/lang/LanguageService";
import { coerceDateToBrowser, coerceDateToTimezone, truncateDate } from "utils/time-utils";
import { ChronoUnit, Instant } from "@js-joda/core";
import { useTimezone } from "hooks/useTimezone";
import { FormEvent } from "primereact/ts-helpers";
import { DEFAULT_CALENDAR_FORMAT } from "utils/constants";
import "react-datepicker/dist/react-datepicker.css";

export type CalendarWrapperProps = {
  value: Date | undefined;
  onChange?(date: Date): void;
  onInstantChange?(instant: Instant): void;
  debounceTimeInMs?: number;
} & Omit<CalendarProps, "value" | "onChange">;

const CalendarWrapper = ({
  value,
  onChange,
  onInstantChange,
  debounceTimeInMs,
  ...props
}: CalendarWrapperProps) => {
  const tz = useTimezone();

  const valueRef = useRef<Date | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const debouncedValue = useDebounce(date, { delay: debounceTimeInMs });

  useEffect(() => {
    const handleEnterPressed = (event: KeyboardEvent) =>
      (event.code === "Enter" || event.code === "NumpadEnter") && replaceDateValue();

    window.addEventListener("keydown", handleEnterPressed);

    return () => window.removeEventListener("keydown", handleEnterPressed);
  }, []);

  useEffect(() => {
    debouncedValue && mainOnChange(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    valueRef.current && (valueRef.current = null);
  }, [value]);

  const mainOnChange = (newDate: Date) => {
    const uncoercedDate = coerceDateToBrowser(newDate, tz.display)!!;
    const instant = uncoercedDate.toInstant();

    onChange?.(uncoercedDate);
    onInstantChange?.(instant);
  };

  const mainSetDate = (newDate: Date) => {
    setDate(newDate);
  };

  const replaceDateValue = () => {
    if (valueRef.current != null && value !== valueRef.current) {
      mainOnChange(valueRef.current);
    }
  };

  const onCalendarChange = (e: FormEvent<Date, React.SyntheticEvent<Element, Event>>) => {
    if (!e.value) return;

    const pickedDate = truncateDate(e.value as Date, ChronoUnit.MINUTES, tz.company);

    if (e.originalEvent?.type === "input") {
      valueRef.current = pickedDate;
    } else {
      mainSetDate(pickedDate);
      mainOnChange(pickedDate);
    }
  };

  const truncateToCompany = (unit: ChronoUnit) => {
    if (!value) return;
    const truncatedDate = truncateDate(value, unit, tz.company);
    mainOnChange(truncatedDate);
  };

  const calendarFooterTemplate = () => {
    return (
      <div className="flex flex-row justify-content-between">
        <Button
          type="button"
          label={T("action.truncate-hours")}
          className="p-button-sm p-button-text p-button-outlined"
          onClick={() => truncateToCompany(ChronoUnit.DAYS)}
        />
        <Button
          type="button"
          label={T("action.truncate-minutes")}
          className="p-button-sm p-button-text p-button-outlined"
          onClick={() => truncateToCompany(ChronoUnit.HOURS)}
        />
      </div>
    );
  };

  return (
    <Calendar
      value={coerceDateToTimezone(value, tz.display)}
      onChange={onCalendarChange}
      footerTemplate={props.showTime === false ? undefined : calendarFooterTemplate}
      showIcon
      iconPos="left"
      dateFormat={DEFAULT_CALENDAR_FORMAT}
      showTime={props.showTime ?? true}
      onBlur={replaceDateValue}
      className={`p-inputtext-sm ${props.className || ""}`}
      {...props}
    />
  );
};

export default CalendarWrapper;
